import React, {useState, useEffect} from 'react';
import {ExpressCheckoutElement, useStripe, useElements} from '@stripe/react-stripe-js';
import Spinner from 'react-bootstrap/Spinner'; 

export const CanAddApplePay = ({subscriptionData, onConfirmPayment, coupon}) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isReady, setIsReady] = useState(false);
    const [error, setError] = useState(null);
    // Initialize currency from subscriptionData if available, otherwise default to 'usd' (lowercase for Stripe)
    const [currency, setCurrency] = useState(
        String(subscriptionData?.currency || subscriptionData?.currency_code || 'USD').toLowerCase()
    );

    useEffect(() => {
      if (!stripe || !elements) {
        return;
      }

      if (!subscriptionData) {
        console.log('Subscription data not available');
        setError('Subscription data is missing');
        return;
      }

      // Check if required fields exist
      const amount = subscriptionData.amount || subscriptionData.price || 0;
      const label = subscriptionData.plan_detail || subscriptionData.name || 'Subscription';
      const currencyValue = String(subscriptionData.currency || subscriptionData.currency_code || 'USD').toLowerCase();
      setCurrency(currencyValue);
      if (!amount || amount <= 0) {
        console.error('Invalid amount:', amount);
        setError('Invalid subscription amount');
        return;
      }

      console.log('Express Checkout Element ready with:', { amount, label, currency: currencyValue, subscriptionData });
      console.log('Stripe instance:', stripe);
      console.log('Elements instance:', elements);
      
      // Check browser and environment
      const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
      const isEdge = /Edg/.test(navigator.userAgent);
      const isHTTPS = window.location.protocol === 'https:' || window.location.hostname === 'localhost';
      
      console.log('Browser check:', {
        isChrome,
        isEdge,
        isHTTPS,
        userAgent: navigator.userAgent,
        protocol: window.location.protocol,
        hostname: window.location.hostname
      });
      
      if (!isChrome && !isEdge) {
        console.warn('Google Pay only works on Chrome or Edge browsers. Current browser may not support Google Pay.');
      }
      
      if (!isHTTPS) {
        console.warn('Google Pay requires HTTPS (or localhost). Current protocol:', window.location.protocol);
      }
      
      setIsReady(true);
      setError(null);
      
    }, [stripe, elements, subscriptionData]);

    // Check available payment methods after element mounts
    useEffect(() => {
      if (isReady && elements) {
        // Small delay to ensure element is mounted
        setTimeout(() => {
          const expressCheckoutElement = elements.getElement('expressCheckout');
          if (expressCheckoutElement) {
            console.log('Express Checkout Element found:', expressCheckoutElement);
            
            // Log available payment methods - the ready event might not have availablePaymentMethods
            // We need to check the element's state differently
            expressCheckoutElement.on('ready', (event) => {
              console.log('Express Checkout ready event:', event);
              
              // Try to get available payment methods from the element's internal state
              // The element might expose this differently
              if (expressCheckoutElement._element) {
                console.log('Element internal state:', expressCheckoutElement._element);
              }
              
              // Check if payment methods are available by trying to access them
              // Express Checkout Element might expose this via a different method
              setTimeout(() => {
                // Try to trigger payment method detection
                console.log('Checking for available payment methods after delay...');
              }, 1000);
              
              if (event && event.availablePaymentMethods) {
                console.log('Available payment methods:', event.availablePaymentMethods);
                console.log('Google Pay available:', event.availablePaymentMethods.googlePay);
                console.log('Apple Pay available:', event.availablePaymentMethods.applePay);
                
                // If no payment methods are available, show error
                if (!event.availablePaymentMethods.applePay && !event.availablePaymentMethods.googlePay) {
                  console.error('No payment methods available in ready event');
                  setError('No payment methods available. Please check your Stripe account settings.');
                }
              } else {
                console.warn('Ready event does not contain availablePaymentMethods:', event);
                console.warn('This might be normal - Express Checkout Element may detect payment methods asynchronously');
              }
              
              // Check the actual DOM for rendered buttons
              setTimeout(() => {
                const container = document.querySelector('[data-testid="express-checkout-element"]') || 
                                 document.querySelector('.express-checkout-element') ||
                                 document.querySelector('[id*="express"]');
                if (container) {
                  console.log('Express Checkout container found:', container);
                  const buttons = container.querySelectorAll('button');
                  console.log('Number of buttons found:', buttons.length);
                  buttons.forEach((btn, index) => {
                    console.log(`Button ${index}:`, {
                      text: btn.textContent,
                      className: btn.className,
                      style: window.getComputedStyle(btn).display,
                      visible: btn.offsetWidth > 0 && btn.offsetHeight > 0
                    });
                  });
                } else {
                  console.log('Express Checkout container not found in DOM');
                }
              }, 1000);
            });
            
            // Note: We don't listen to 'click' event because we use 'onConfirm' prop instead
            // If you need to listen to click, you must call event.resolve() or event.reject() within 1 second

            // Also check browser support directly
            if (window.PaymentRequest) {
              console.log('Payment Request API is supported');
              const pr = new window.PaymentRequest(
                [{ supportedMethods: 'https://google.com/pay' }],
                { total: { label: 'Test', amount: { currency: 'USD', value: '1.00' } } }
              );
              pr.canMakePayment().then(result => {
                console.log('Google Pay canMakePayment result:', result);
              }).catch(err => {
                console.log('Google Pay canMakePayment error:', err);
              });
            } else {
              console.log('Payment Request API is NOT supported in this browser');
            }
          } else {
            console.log('Express Checkout Element not found yet');
          }
        }, 500);
      }
    }, [isReady, elements]);

    const handleConfirm = async (event) => {
      console.log('handleConfirm called with event:', event);
      
      if (!stripe || !elements) {
        console.error('Stripe or elements not available');
        if (event && typeof event.reject === 'function') {
          event.reject('Stripe not initialized');
        }
        return;
      }

      try {
        // Submit the form first
        const {error: submitError} = await elements.submit();
        if (submitError) {
          console.error('Submit error:', submitError);
          setError(submitError.message);
          if (event && typeof event.reject === 'function') {
            event.reject(submitError.message);
          }
          return;
        }

        // Confirm Setup Intent with the payment method from Express Checkout
        const {error: confirmError, setupIntent} = await stripe.confirmSetup({
          elements,
          redirect: 'if_required',
        });

        if (confirmError) {
          console.error('Setup confirmation error:', confirmError);
          setError(confirmError.message);
          if (event && typeof event.reject === 'function') {
            event.reject(confirmError.message);
          }
          return;
        }

        if (setupIntent && setupIntent.payment_method) {
          console.log('Setup Intent successful, payment_method:', setupIntent.payment_method);
          
          // Create event object compatible with existing handler
          // The parent expects event.paymentMethod.id and will call event.complete()
          const mockEvent = {
            paymentMethod: {
              id: setupIntent.payment_method
            },
            complete: (status) => {
              console.log('Payment completed with status:', status);
              // Resolve or reject the Express Checkout event based on status
              if (event && typeof event.resolve === 'function' && status === 'success') {
                event.resolve();
              } else if (event && typeof event.reject === 'function' && status === 'fail') {
                event.reject('Payment processing failed');
              }
            }
          };
          
          // Pass the event to parent - it will process payment and call complete()
          // We don't resolve here - we wait for the parent to call complete()
          onConfirmPayment(mockEvent,coupon);
        } else {
          console.error('No payment method in setupIntent');
          setError('No payment method received');
          if (event && typeof event.reject === 'function') {
            event.reject('No payment method received');
          }
        }
      } catch (err) {
        console.error('Error in handleConfirm:', err);
        setError(err.message || 'An error occurred during payment');
        if (event && typeof event.reject === 'function') {
          event.reject(err.message || 'An error occurred during payment');
        }
      }
    };

    // Ensure currency is always a valid lowercase string
    const validCurrency = typeof currency === 'string' && currency.length > 0 ? currency.toLowerCase() : 'usd';

    if (!isReady) {
      if (error) {
        return <div className="text-3" style={{textAlign: 'center', color: '#ff6b6b'}}>Error: {error}</div>;
      }
      return <div className="" style={{textAlign: 'center'}}><Spinner animation="border" size="sm" variant="dark" /></div>;
    }

    // Don't render if currency is not valid
    if (!validCurrency || typeof validCurrency !== 'string') {
      return <div className="text-3" style={{textAlign: 'center', color: '#ff6b6b'}}>Error: Invalid currency configuration</div>;
    }

    return (
      <div style={{minHeight: '60px', width: '100%'}}>
        <ExpressCheckoutElement
          onConfirm={handleConfirm}
          onReady={(event) => {
            console.log('ExpressCheckoutElement onReady:', event);
            console.log('onReady event keys:', event ? Object.keys(event) : 'no event');
            
            // The onReady event might not have availablePaymentMethods directly
            // Check if we can get it from the element
            if (elements) {
              const expressElement = elements.getElement('expressCheckout');
              if (expressElement) {
                // Try to get available payment methods from the element
                expressElement.on('ready', (readyEvent) => {
                  console.log('Element ready event:', readyEvent);
                  if (readyEvent && readyEvent.availablePaymentMethods) {
                    console.log('Available payment methods from element ready:', readyEvent.availablePaymentMethods);
                  }
                });
              }
            }
            
            if (event && event.availablePaymentMethods) {
              console.log('Available payment methods from onReady:', event.availablePaymentMethods);
              console.log('Google Pay in onReady:', event.availablePaymentMethods.googlePay);
              console.log('Apple Pay in onReady:', event.availablePaymentMethods.applePay);
              
              // Check if no payment methods are available
              const hasApplePay = event.availablePaymentMethods.applePay === true;
              const hasGooglePay = event.availablePaymentMethods.googlePay === true;
              
              if (!hasApplePay && !hasGooglePay) {
                console.warn('No payment methods available in onReady event');
                setError('No payment methods available. Please ensure Apple Pay or Google Pay is set up.');
              } else {
                console.log('Payment methods available - Apple Pay:', hasApplePay, 'Google Pay:', hasGooglePay);
              }
            } else {
              console.warn('onReady event does not have availablePaymentMethods. Event:', event);
            }
            
            // Check DOM after a delay to see what's actually rendered
            setTimeout(() => {
              // Look for Stripe's express checkout buttons
              const stripeButtons = document.querySelectorAll('[class*="StripeElement"], [class*="express"], [class*="google"], [class*="apple"]');
              console.log('Found Stripe-related elements:', stripeButtons.length);
              stripeButtons.forEach((el, i) => {
                console.log(`Element ${i}:`, {
                  tag: el.tagName,
                  class: el.className,
                  text: el.textContent?.substring(0, 50),
                  display: window.getComputedStyle(el).display,
                  visibility: window.getComputedStyle(el).visibility,
                  width: el.offsetWidth,
                  height: el.offsetHeight
                });
              });
              
              // Also check for iframes (Stripe might use iframes)
              const iframes = document.querySelectorAll('iframe');
              console.log('Found iframes:', iframes.length);
              iframes.forEach((iframe, i) => {
                if (iframe.src && (iframe.src.includes('stripe') || iframe.src.includes('js.stripe'))) {
                  console.log(`Stripe iframe ${i}:`, iframe.src);
                  
                  // Try to access iframe content (will fail due to CORS, but worth trying)
                  try {
                    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
                    if (iframeDoc) {
                      const buttons = iframeDoc.querySelectorAll('button');
                      console.log(`Buttons inside iframe ${i}:`, buttons.length);
                      buttons.forEach((btn, idx) => {
                        console.log(`  Button ${idx}:`, {
                          text: btn.textContent,
                          class: btn.className,
                          visible: btn.offsetWidth > 0
                        });
                      });
                    }
                  } catch (e) {
                    console.log(`Cannot access iframe ${i} content (CORS):`, e.message);
                  }
                }
              });
              
              // Check if Express Checkout Element container exists
              const expressContainer = document.querySelector('[id*="expressCheckout"]') || 
                                      document.querySelector('[class*="express"]');
              if (expressContainer) {
                console.log('Express Checkout container:', {
                  id: expressContainer.id,
                  class: expressContainer.className,
                  width: expressContainer.offsetWidth,
                  height: expressContainer.offsetHeight,
                  children: expressContainer.children.length
                });
              }
            }, 2000);
          }}
          options={{
            currency: validCurrency,
            buttonHeight: 48,
            buttonType: {
              applePay: 'subscribe',
              googlePay: 'subscribe',
            },
            paymentMethods: {
              applePay: 'always',
              googlePay: 'always',
              link: 'never',
              paypal: 'never',
              amazonPay: 'never',
            },
            business: {
              name: 'Club Killers',
            },
          }}
          onLoaderStart={() => console.log('Express Checkout loader started')}
          onLoaderEnd={() => console.log('Express Checkout loader ended')}
        />
        {error && (
          <div className="text-3" style={{textAlign: 'center', color: '#ff6b6b', marginTop: '10px'}}>
            {error}
          </div>
        )}
        <div className="text-5" style={{textAlign: 'center', marginTop: '10px', fontSize: '12px', color: '#666'}}>
          {typeof window !== 'undefined' && (
            <>
              {!window.location.protocol.includes('https') && window.location.hostname !== 'localhost' && (
                <div>Note: Google Pay requires HTTPS connection</div>
              )}
              {(/Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent) && !/Edg/.test(navigator.userAgent)) && (
                <div>Note: Google Pay is not available in Safari. Please use Chrome or Edge for Google Pay.</div>
              )}
              {(/Macintosh|Mac OS X/.test(navigator.userAgent) && /Chrome/.test(navigator.userAgent)) && (
                <div style={{fontSize: '11px', marginTop: '5px'}}>
                  Note: On macOS, Stripe may prioritize Apple Pay.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
}